import { Controller } from "@hotwired/stimulus";

const EMAIL_PATTERN = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,}$/;
const EMAIL_MAX_LENGTH = 255;
const MESSAGE_MIN_LENGTH = 50;
const MESSAGE_MAX_LENGTH = 1000;
const FILE_MAX_SIZE = 20; // MB
const ALERT_HIDE_DELAY = 3000;

export default class extends Controller {
  static targets = [
    "form",
    "firstNameField",
    "emailField",
    "emailFieldError",
    "subjectField",
    "subjectFieldLabel",
    "subjectFieldNotch",
    "messageField",
    "messageFieldHint",
    "messageFieldError",
    "messageLength",
    "fileField",
    "fileFieldHint",
    "fileFieldError",
    "fileName",
    "fileSize",
    "uploadArea",
    "uploadInfo",
    "submitBtn",
    "submitSuccessAlert",
  ];

  static values = {
    messageTooShort: String,
    messageTooLong: String,
    initialFirstName: String,
    initialEmail: String,
  };

  invalidFields = new Set([]);

  connect() {
    this.initSelectNotch();

    this.firstNameFieldTarget.value = this.initialFirstNameValue;
    this.firstNameFieldTarget.classList.toggle("active", !!this.initialFirstNameValue);
    this.emailFieldTarget.value = this.initialEmailValue;
    this.emailFieldTarget.classList.toggle("active", !!this.initialEmailValue);
  }

  initSelectNotch() {
    if (this.hasSubjectFieldLabelTarget && this.hasSubjectFieldNotchTarget) {
      this.subjectFieldNotchTarget.style.width = (this.subjectFieldLabelTarget.clientWidth * 0.8 + 8) + "px";
    }
  }

  isEmailValid() {
    const email = this.emailFieldTarget.value.trim();
    return email.length > 0 && EMAIL_PATTERN.test(email) && email.length <= EMAIL_MAX_LENGTH;
  }

  validateEmail() {
    const isValid = this.isEmailValid();
    
    this.invalidFields[isValid ? "delete" : "add"]("email");
    this.submitBtnTarget.disabled = this.invalidFields.size > 0;

    this.emailFieldErrorTarget.classList.toggle("d-none", isValid);
    this.emailFieldTarget.classList.toggle("is-invalid", !isValid);
  }

  isMessageTooShort() {
    const messageLength = this.messageFieldTarget.value.length;

    return messageLength < MESSAGE_MIN_LENGTH;
  }

  isMessageTooLong() {
    const messageLength = this.messageFieldTarget.value.length;

    return messageLength > MESSAGE_MAX_LENGTH;
  }

  validateMessage() {
    const isMessageTooShort = this.isMessageTooShort();
    const isMessageTooLong = this.isMessageTooLong();
    const isValid = !isMessageTooShort && !isMessageTooLong;

    this.invalidFields[isValid ? "delete" : "add"]("message");
    this.submitBtnTarget.disabled = this.invalidFields.size > 0;

    this.messageFieldErrorTarget.textContent = isMessageTooShort
      ? this.messageTooShortValue
      : isMessageTooLong
        ? this.messageTooLongValue
        : "";

    this.messageFieldErrorTarget.classList.toggle("d-none", isValid);
    this.messageFieldHintTarget.classList.toggle("d-none", !isValid);
    this.messageFieldTarget.classList.toggle("is-invalid", !isValid);
  }

  updateMessageLength() {
    this.messageLengthTarget.textContent = this.messageFieldTarget.value.length;
  }

  resizeMessageField() {
    this.messageFieldTarget.style.height = "auto";
    this.messageFieldTarget.style.height = this.messageFieldTarget.scrollHeight + "px";
  }

  formatFileSize(bytes) {
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(2) + " KB";
    }

    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  }

  displayFileDetails() {
    const file = this.fileFieldTarget.files?.[0];

    if (!file) return;

    this.fileNameTarget.textContent = file.name;
    this.fileSizeTarget.textContent = this.formatFileSize(file.size);
  }

  showUploadInfo() {
    this.displayFileDetails();

    this.uploadAreaTarget.classList.add("d-none");
    this.uploadInfoTarget.classList.remove("d-none");
  }

  isValidFileSize() {
    const file = this.fileFieldTarget.files?.[0];

    if (!file) return false;

    return file.size <= FILE_MAX_SIZE * 1024 * 1024;
  }

  validateFile() {
    const isValid = this.isValidFileSize();

    this.invalidFields[isValid ? "delete" : "add"]("file");
    this.submitBtnTarget.disabled = this.invalidFields.size > 0;

    this.fileFieldErrorTarget.classList.toggle("d-none", isValid);
    this.fileFieldHintTarget.classList.toggle("d-none", !isValid);
    this.uploadInfoTarget.classList.toggle("text-success", isValid);
    this.uploadInfoTarget.classList.toggle("border-success", isValid);
    this.uploadInfoTarget.classList.toggle("text-danger", !isValid);
    this.uploadInfoTarget.classList.toggle("border-danger", !isValid);
  }

  removeFile() {
    this.fileFieldTarget.value = "";
    this.fileNameTarget.textContent = "";
    this.fileSizeTarget.textContent = "";

    this.invalidFields.delete("file");
    this.submitBtnTarget.disabled = this.invalidFields.size > 0;

    this.fileFieldErrorTarget.classList.add("d-none");
    this.fileFieldHintTarget.classList.remove("d-none");
    this.uploadAreaTarget.classList.remove("d-none");
    this.uploadInfoTarget.classList.add("d-none");
    this.uploadInfoTarget.classList.remove("text-success");
    this.uploadInfoTarget.classList.remove("border-success");
    this.uploadInfoTarget.classList.remove("text-danger");
    this.uploadInfoTarget.classList.remove("border-danger");
  }

  resetForm() {
    this.firstNameFieldTarget.value = this.initialFirstNameValue;
    this.emailFieldTarget.value = this.initialEmailValue;
    this.subjectFieldTarget.value = "";
    this.messageFieldTarget.value = "";
    this.messageLengthTarget.innerText = "0";
    this.removeFile();
    this.invalidFields.clear();
  }

  buildFormDataRequest(variables, file) {
    const formData = new FormData();

    const operations = JSON.stringify({
      query: `
        mutation($name: String, $email: String, $subject: String, $messageText: String!, $file: Upload) {
          sendSupportMessage(
            input: {
              name: $name,
              email: $email
              subject: $subject
              messageText: $messageText
              file: $file
            }
          )
        }
      `,
      variables: { ...variables, file: null },
    });

    const map = JSON.stringify({
      "0": ["variables.file"],
    });

    formData.append("operations", operations);
    formData.append("map", map);
    formData.append("0", file, file.name);

    return { method: "POST", body: formData };
  }

  buildJsonRequest(variables) {
    return {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: `
          mutation($name: String, $email: String, $subject: String, $messageText: String!) {
            sendSupportMessage(
              input: {
                name: $name,
                email: $email
                subject: $subject
                messageText: $messageText
              }
            )
          }
        `,
        variables,
      }),
    };
  }

  buildRequestInit() {
    const variables = {
      name: this.firstNameFieldTarget.value.trim(),
      email: this.emailFieldTarget.value.trim(),
      subject: this.subjectFieldTarget.value,
      messageText: this.messageFieldTarget.value.trim(),
    };

    const file = this.fileFieldTarget.files?.[0];

    return file ? this.buildFormDataRequest(variables, file) : this.buildJsonRequest(variables);
  }

  flashSuccessSubmitAlert() {
    this.submitSuccessAlertTarget.classList.add("show");
    setTimeout(() => this.submitSuccessAlertTarget.classList.remove("show"), ALERT_HIDE_DELAY);
  }

  closeSuccessSubmitAlert() {
    this.submitSuccessAlertTarget.classList.remove("show");
  }

  async submitForm() {
    this.validateEmail();
    this.validateMessage();

    if (this.invalidFields.size > 0) return;

    this.submitBtnTarget.disabled = true;
    this.formTarget.inert = true;

    try {
      const response = await fetch("/graphql/", this.buildRequestInit());
      const result = await response.json();

      if (result.errors) {
        console.error(result.errors);
        throw new Error(result.errors[0].message);
      }

      this.resetForm();
      this.flashSuccessSubmitAlert();
      
    } catch (error) {
      console.error(error);
    } finally {
      this.submitBtnTarget.disabled = false;
      this.formTarget.inert = false;
    }
  }
}
